import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
} from '@mui/material'
import {
  Add,
  Edit,
  Visibility,
  Groups,
  Delete as DeleteIcon,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery, useCreateTeamMutation, useUpdateTeamMutation, useGetTeamCasesQuery, type Team, type TeamCreate } from '../store/api/teamsApi'
import { useLazyGetUserByEmailQuery, useGetUsersBatchQuery, useFetchUsersBatchMutation } from '../store/api/usersApi'
import { hasPermission } from '../utils/permissions'

const STATUS_OPTIONS = [
  { value: 'registered', label: 'Đã đăng ký' },
  { value: 'confirmed', label: 'Đã xác nhận' },
  { value: 'active', label: 'Đang hoạt động' },
  { value: 'completed', label: 'Đã hoàn thành' },
]

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  registered: 'info',
  confirmed: 'primary',
  active: 'success',
  completed: 'default',
}

interface MemberEmail {
  email: string
  userId?: string
  fullName?: string
  role?: string
  isValid?: boolean
  isLoading?: boolean
  error?: string
}

export default function TeamsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: teams = [], isLoading, error, refetch } = useGetTeamsQuery()
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation()
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [fetchUsersBatch] = useFetchUsersBatchMutation()
  
  // Check permissions
  const canRegisterTeam = currentUser && hasPermission(currentUser.role, 'REGISTER_TEAM') // Student đăng ký team
  const canManageTeam = currentUser && hasPermission(currentUser.role, 'MANAGE_TEAM')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canViewAllTeams = canAssignCases // Secretary/Support can view all teams
  const isSecretary = currentUser?.role === 'secretary' // Only secretary can change manager

  // Get current user's team
  const userTeam = teams.find(t => 
    t.members.includes(currentUser?.id || '') || 
    t.coach_id === currentUser?.id
  )
  
  // Get users for display - only for teams that current user can view
  const displayTeams = canViewAllTeams 
    ? teams 
    : userTeam 
      ? [userTeam]
      : []
  
  // Collect all unique user IDs from displayTeams (members + coaches)
  const allUserIds = displayTeams.flatMap(team => [
    ...team.members,
    ...(team.coach_id ? [team.coach_id] : [])
  ]).filter((id, index, arr) => arr.indexOf(id) === index && id) // Remove duplicates and empty
  
  const { data: users = [] } = useGetUsersBatchQuery(allUserIds, { skip: allUserIds.length === 0 || isLoading })

  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [viewingTeam, setViewingTeam] = useState<Team | null>(null)
  const [formData, setFormData] = useState<TeamCreate>({
    name: '',
    university: '',
    members: [],
    coach_id: '',
  })
  const [memberEmails, setMemberEmails] = useState<MemberEmail[]>([])
  const [coachEmail, setCoachEmail] = useState<string>('')
  const [coachInfo, setCoachInfo] = useState<{ id?: string; name?: string; isValid?: boolean; isLoading?: boolean; error?: string }>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!canRegisterTeam && !canViewAllTeams && !userTeam) {
      setErrorMessage('Bạn không có quyền xem danh sách teams. Chỉ có thể xem team của mình.')
    }
  }, [canRegisterTeam, canViewAllTeams, userTeam])

  const handleOpenDialog = async (team?: Team) => {
    if (team) {
      setEditingTeam(team)
      setFormData({
        name: team.name,
        university: team.university,
        members: team.members,
        coach_id: team.coach_id || '',
      })
      
      // Fetch user info for team members (use batch API)
      try {
        const teamMemberIds = team.members.filter(id => id)
        const coachId = team.coach_id ? [team.coach_id] : []
        const allIds = [...teamMemberIds, ...coachId]
        
        if (allIds.length > 0) {
          // Use RTK Query mutation to fetch users
          const fetchedUsers = await fetchUsersBatch(allIds).unwrap()
          
          // Populate memberEmails from fetched users
          const memberEmailsData: MemberEmail[] = team.members.map(memberId => {
            const userInfo = fetchedUsers.find((u: any) => u.id === memberId)
            if (userInfo) {
              return {
                email: userInfo.email,
                userId: userInfo.id,
                fullName: userInfo.full_name || userInfo.username,
                role: userInfo.role,
                isValid: true,
              }
            } else {
              return {
                email: '',
                userId: memberId,
                isValid: false,
              }
            }
          })
          setMemberEmails(memberEmailsData)
          
          // Populate coach info if exists
          if (team.coach_id) {
            const coachUser = fetchedUsers.find((u: any) => u.id === team.coach_id)
            if (coachUser) {
              setCoachEmail(coachUser.email)
              setCoachInfo({
                id: coachUser.id,
                name: coachUser.full_name || coachUser.username,
                isValid: true,
              })
            } else {
              setCoachEmail('')
              setCoachInfo({})
            }
          } else {
            setCoachEmail('')
            setCoachInfo({})
          }
        } else {
          setMemberEmails([])
          setCoachEmail('')
          setCoachInfo({})
        }
      } catch (err) {
        console.error('Error fetching user info for team members:', err)
        // Fallback: use users from batch query if available
        const memberEmailsData: MemberEmail[] = team.members.map(memberId => {
          const userInfo = users.find(u => u.id === memberId)
          return userInfo ? {
            email: userInfo.email,
            userId: userInfo.id,
            fullName: userInfo.full_name || userInfo.username,
            role: userInfo.role,
            isValid: true,
          } : {
            email: '',
            userId: memberId,
            isValid: false,
          }
        })
        setMemberEmails(memberEmailsData)
        
        if (team.coach_id) {
          const coachUser = users.find(u => u.id === team.coach_id)
          if (coachUser) {
            setCoachEmail(coachUser.email)
            setCoachInfo({
              id: coachUser.id,
              name: coachUser.full_name || coachUser.username,
              isValid: true,
            })
          } else {
            setCoachEmail('')
            setCoachInfo({})
          }
        } else {
          setCoachEmail('')
          setCoachInfo({})
        }
      }
    } else {
      setEditingTeam(null)
      setFormData({
        name: '',
        university: '',
        members: currentUser?.role === 'student' ? [currentUser.id] : [], // Student tự động là member đầu tiên
        coach_id: '',
      })
      // Initialize với current user email (disabled)
      if (currentUser?.role === 'student' && currentUser.email) {
        setMemberEmails([{
          email: currentUser.email,
          userId: currentUser.id,
          fullName: currentUser.full_name || currentUser.username,
          role: currentUser.role,
          isValid: true,
        }])
      } else {
        setMemberEmails([])
      }
      setCoachEmail('')
      setCoachInfo({})
    }
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingTeam(null)
    setFormData({
      name: '',
      university: '',
      members: [],
      coach_id: '',
    })
    setMemberEmails([])
    setCoachEmail('')
    setCoachInfo({})
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleViewTeam = (team: Team) => {
    setViewingTeam(team)
    setViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setViewDialog(false)
    setViewingTeam(null)
  }

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  // Handle member email change and lookup
  const handleMemberEmailChange = async (index: number, email: string) => {
    // Prevent changing manager (first member) if not secretary
    if (index === 0 && editingTeam && !isSecretary) {
      return
    }

    const newMemberEmails = [...memberEmails]
    newMemberEmails[index] = {
      email,
      isLoading: true,
      isValid: false,
    }
    setMemberEmails(newMemberEmails)

    // Validate email format
    if (!isValidEmail(email)) {
      newMemberEmails[index] = {
        email,
        isValid: false,
        isLoading: false,
        error: 'Email không hợp lệ',
      }
      setMemberEmails(newMemberEmails)
      return
    }

    try {
      const result = await getUserByEmail(email).unwrap()
      if (result.role === 'student') {
        newMemberEmails[index] = {
          email,
          userId: result.id,
          fullName: result.full_name || result.username,
          role: result.role,
          isValid: true,
          isLoading: false,
        }
        // Update formData members
        const newMembers = [...formData.members]
        if (index === 0 && !editingTeam && currentUser?.role === 'student') {
          // First member is always current user when creating
          newMembers[0] = currentUser.id
        } else {
          newMembers[index] = result.id
        }
        setFormData({ ...formData, members: newMembers })
      } else {
        newMemberEmails[index] = {
          email,
          isValid: false,
          isLoading: false,
          error: 'Chỉ có thể thêm sinh viên vào đội thi',
        }
      }
    } catch (err: any) {
      newMemberEmails[index] = {
        email,
        isValid: false,
        isLoading: false,
        error: err?.data?.detail || 'Không tìm thấy người dùng với email này',
      }
    }
    setMemberEmails(newMemberEmails)
  }

  // Handle coach email change and lookup
  const handleCoachEmailChange = async (email: string) => {
    setCoachEmail(email)
    setCoachInfo({ isLoading: true, isValid: false })

    if (!email) {
      setCoachInfo({})
      setFormData({ ...formData, coach_id: '' })
      return
    }

    // Validate email format
    if (!isValidEmail(email)) {
      setCoachInfo({ isValid: false, error: 'Email không hợp lệ' })
      return
    }

    try {
      const result = await getUserByEmail(email).unwrap()
      if (result.role === 'coach') {
        setCoachInfo({
          id: result.id,
          name: result.full_name || result.username,
          isValid: true,
        })
        setFormData({ ...formData, coach_id: result.id })
      } else {
        setCoachInfo({
          isValid: false,
          error: 'Chỉ có thể chọn huấn luyện viên',
        })
      }
    } catch (err: any) {
      setCoachInfo({
        isValid: false,
        error: err?.data?.detail || 'Không tìm thấy huấn luyện viên với email này',
      })
    }
  }

  const handleAddMember = () => {
    setMemberEmails([...memberEmails, { email: '', isValid: false }])
    setFormData({ ...formData, members: [...formData.members, ''] })
  }

  const handleRemoveMember = (index: number) => {
    // Cannot remove first member (current user) when creating new team
    if (index === 0 && !editingTeam && currentUser?.role === 'student') {
      return
    }
    // Cannot remove manager (first member) when editing unless secretary
    if (index === 0 && editingTeam && !isSecretary) {
      return
    }
    const newMemberEmails = memberEmails.filter((_, i) => i !== index)
    const newMembers = formData.members.filter((_, i) => i !== index)
    setMemberEmails(newMemberEmails)
    setFormData({ ...formData, members: newMembers })
  }

  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    // Validation
    if (!formData.name.trim()) {
      setErrorMessage('Tên đội là bắt buộc')
      return
    }
    if (!formData.university.trim()) {
      setErrorMessage('Trường đại học là bắt buộc')
      return
    }
    
    // Build final members list from memberEmails
    const validMembers: string[] = []
    for (let i = 0; i < memberEmails.length; i++) {
      const memberEmail = memberEmails[i]
      if (memberEmail.userId) {
        validMembers.push(memberEmail.userId)
      } else if (i === 0 && currentUser?.role === 'student' && currentUser.id) {
        // First member is always current user
        validMembers.push(currentUser.id)
      } else if (memberEmail.email) {
        setErrorMessage(`Vui lòng kiểm tra lại email của thành viên ${i + 1}: ${memberEmail.email}`)
        return
      }
    }
    
    if (validMembers.length === 0) {
      setErrorMessage('Phải có ít nhất 1 thành viên')
      return
    }

    // Prepare final form data
    const finalFormData: TeamCreate = {
      name: formData.name.trim(),
      university: formData.university.trim(),
      members: validMembers,
      coach_id: coachInfo.id || undefined,
    }

    try {
      if (editingTeam) {
        await updateTeam({ id: editingTeam.id, ...finalFormData }).unwrap()
        setSuccessMessage('Cập nhật đội thi thành công!')
      } else {
        await createTeam(finalFormData).unwrap()
        setSuccessMessage('Đăng ký đội thi thành công!')
      }
      handleCloseDialog()
      refetch()
    } catch (err: any) {
      console.error('[Team Registration Error]', err)
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi đăng ký đội thi')
    }
  }


  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Groups sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Quản lý Đội thi
          </Typography>
        </Box>
        {canRegisterTeam && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              px: 3,
              py: 1,
            }}
          >
            Đăng ký đội mới
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: { xs: 1, sm: 2 } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Tên đội</strong></TableCell>
                <TableCell><strong>Trường đại học</strong></TableCell>
                <TableCell><strong>Số thành viên</strong></TableCell>
                <TableCell><strong>Huấn luyện viên</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell><strong>Số cuộc thi</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTeams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {canRegisterTeam ? 'Chưa có đội thi nào. Hãy đăng ký đội mới!' : 'Bạn chưa tham gia đội thi nào.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayTeams.map((team) => {
                  const coach = team.coach_id ? users.find(u => u.id === team.coach_id) : null
                  return (
                    <TableRow key={team.id} hover>
                      <TableCell>{team.name}</TableCell>
                      <TableCell>{team.university}</TableCell>
                      <TableCell>{team.members.length}</TableCell>
                      <TableCell>{coach ? (coach.full_name || coach.username) : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_OPTIONS.find(s => s.value === team.status)?.label || team.status}
                        color={STATUS_COLORS[team.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{team.assigned_cases?.length || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewTeam(team)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                      {((canManageTeam && userTeam?.id === team.id) || canAssignCases) && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(team)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          {editingTeam ? 'Chỉnh sửa đội thi' : 'Đăng ký đội thi mới'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            <TextField
              label="Tên đội"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Trường đại học"
              value={formData.university}
              onChange={(e) => setFormData({ ...formData, university: e.target.value })}
              required
              fullWidth
            />
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Thành viên
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleAddMember}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Thêm thành viên
                </Button>
              </Box>
              {memberEmails.map((memberEmail, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label={`Thành viên ${index + 1}${index === 0 && !editingTeam && currentUser?.role === 'student' ? ' (Bạn)' : ''}${index === 0 && editingTeam ? ' (Quản lý đội)' : ''}`}
                      type="email"
                      value={memberEmail.email}
                      onChange={(e) => {
                        const newEmails = [...memberEmails]
                        newEmails[index] = { ...memberEmails[index], email: e.target.value }
                        setMemberEmails(newEmails)
                      }}
                      onBlur={(e) => {
                        if (e.target.value && e.target.value !== memberEmail.email) {
                          handleMemberEmailChange(index, e.target.value)
                        }
                      }}
                      disabled={
                        !!(index === 0 && !editingTeam && currentUser?.role === 'student') || // First member when creating (student)
                        !!(index === 0 && editingTeam && !isSecretary) // Manager when editing (only secretary can change)
                      }
                      required
                      fullWidth
                      error={memberEmail.error !== undefined}
                      helperText={
                        memberEmail.isLoading 
                          ? 'Đang tìm kiếm...' 
                          : index === 0 && editingTeam && !isSecretary
                            ? 'Chỉ thư ký mới có thể thay đổi người quản lý đội thi'
                            : memberEmail.error || (memberEmail.isValid && memberEmail.fullName ? `${memberEmail.fullName} (${memberEmail.role})` : '')
                      }
                      InputProps={{
                        endAdornment: memberEmail.isLoading ? (
                          <CircularProgress size={20} />
                        ) : memberEmail.isValid ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : memberEmail.error ? (
                          <ErrorIcon color="error" fontSize="small" />
                        ) : null,
                      }}
                    />
                  </Box>
                  {memberEmails.length > 1 && !(index === 0 && !editingTeam && currentUser?.role === 'student') && !(index === 0 && editingTeam && !isSecretary) && (
                    <IconButton onClick={() => handleRemoveMember(index)} color="error" size="small" sx={{ mt: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Huấn luyện viên (tùy chọn)
              </Typography>
              <TextField
                label="Email huấn luyện viên"
                type="email"
                value={coachEmail}
                onChange={(e) => setCoachEmail(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value !== coachEmail) {
                    handleCoachEmailChange(e.target.value)
                  }
                }}
                fullWidth
                error={coachInfo.error !== undefined}
                helperText={
                  coachInfo.isLoading 
                    ? 'Đang tìm kiếm...' 
                    : coachInfo.error || (coachInfo.isValid && coachInfo.name ? coachInfo.name : 'Nhập email của huấn luyện viên')
                }
                InputProps={{
                  endAdornment: coachInfo.isLoading ? (
                    <CircularProgress size={20} />
                  ) : coachInfo.isValid ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : coachInfo.error ? (
                    <ErrorIcon color="error" fontSize="small" />
                  ) : null,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : (editingTeam ? 'Cập nhật' : 'Đăng ký')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Team Dialog */}
      {viewingTeam && (
        <TeamDetailDialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          team={viewingTeam}
        />
      )}
    </Container>
  )
}

// Team Detail Dialog Component
function TeamDetailDialog({ 
  open, 
  onClose, 
  team, 
}: { 
  open: boolean
  onClose: () => void
  team: Team
}) {
  const { data: teamCases = [] } = useGetTeamCasesQuery(team.id, { skip: !open })
  
  // Get users for team members and coach
  const memberIds = team.members
  const coachId = team.coach_id ? [team.coach_id] : []
  const allUserIds = [...memberIds, ...coachId]
  const { data: users = [] } = useGetUsersBatchQuery(allUserIds, { skip: !open || allUserIds.length === 0 })
  
  const teamMembers = team.members.map(id => users.find(u => u.id === id)).filter(Boolean)
  const coach = team.coach_id ? users.find(u => u.id === team.coach_id) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups />
          <Typography variant="h6">{team.name}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trường đại học
            </Typography>
            <Typography variant="body1">{team.university}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Thành viên ({team.members.length} người)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {teamMembers.map((member, index) => (
                <Chip
                  key={member?.id || index}
                  label={`${member?.full_name || member?.username} (${member?.email})`}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {coach && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Huấn luyện viên
              </Typography>
              <Typography variant="body1">
                {coach.full_name || coach.username} ({coach.email})
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trạng thái
            </Typography>
            <Chip
              label={STATUS_OPTIONS.find(s => s.value === team.status)?.label || team.status}
              color={STATUS_COLORS[team.status] || 'default'}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cuộc thi được phân bổ ({teamCases.length})
            </Typography>
            {teamCases.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Chưa có cuộc thi nào được phân bổ
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {teamCases.map((caseItem: any) => (
                  <Paper key={caseItem.id} sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {caseItem.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trạng thái: {caseItem.status} | Deadline: {caseItem.deadline ? new Date(caseItem.deadline).toLocaleDateString('vi-VN') : 'Chưa có'}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
        >
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  )
}
